import React,{useState} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { FiEye, FiEyeOff } from "react-icons/fi";
import {
  AccountPopup,
  AddPhoto,
  HiddenFileInput,
  Input,
  PhotoLabel,
  PopupButton,
} from "./style";
import { createNewAdmin } from "../../services/admins";
import { ErrorMsg ,Title } from "./style";
import toast from "react-hot-toast";
import { uploadImage } from "../../services/upload";

const SignupForm = ({ onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const createAdminMutation = useMutation({
    mutationFn: async (formData) => {
      const res = await createNewAdmin(formData);
      return res;
    },
    onSuccess: () => {
      toast.success("✅ تم إنشاء الحساب بنجاح 🎉", {
        icon: "🆕",
        style: {
          fontFamily: "Noto Kufi Arabic",
          direction: "rtl",
        },
      });
      onClose();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.msg.msg.email|| err?.response?.data?.msg.msg.phone_number || "❌ حدث خطأ أثناء إنشاء الحساب",
        {
          style: {
            fontFamily: "Noto Kufi Arabic",
            direction: "rtl",
          },
        }
      );
    },
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      phone_number: "",
      email: "",
      password: "",
      confirm_password: "",
      profile_img: "",
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .max(20, "الاسم يجب ألا يزيد عن 20 حرفًا")
        .required("الاسم مطلوب"),

      phone_number: Yup.string()
        .matches(/^\d+$/, "رقم الهاتف يجب أن يحتوي على أرقام فقط")
        .required("رقم الهاتف مطلوب"),

      email: Yup.string()
        .email("يجب إدخال بريد إلكتروني صالح")
        .required("البريد الإلكتروني مطلوب"),

      password: Yup.string()
        .required("كلمة المرور مطلوبة")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
          "كلمة المرور يجب أن تكون 8 أحرف على الأقل وتحتوي على حرف كبير وآخر صغير"
        ),

      confirm_password: Yup.string()
        .oneOf([Yup.ref("password")], "كلمتا المرور غير متطابقتين")
        .required("تأكيد كلمة المرور مطلوب"),

      profile_img: Yup.mixed()
        .required("الصورة الشخصية مطلوبة")
        .test(
          "fileType",
          "يجب أن تكون الصورة من نوع صالح (jpeg/png)",
          (value) =>
            value &&
            ["image/jpeg", "image/png", "image/jpg"].includes(value.type)
        ),
    }),

    onSubmit: async (values) => {
      try {
    
       let public_image = "";

    if (values.profile_img) {
      public_image = await uploadImage(values.profile_img)
  
    }

        const payload = {
          name: values.name.trim().slice(0, 20),
          phone_number: Number(values.phone_number),
          email: values.email.trim(),
          password: values.password,
          is_active: false,
          profile_img: public_image,
        };
        createAdminMutation.mutate(payload);
      } catch (error) {
        console.error("Error converting image:", error);
      }
    },
  });

  return (
    <AccountPopup
      as="form"
      onSubmit={formik.handleSubmit}
      onClick={(e) => e.stopPropagation()}
    >
      <Title> إضافة حساب أدمن جديد</Title>

      {/* Name */}
      <div>
        <Input
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          placeholder="الاسم"
          hasError={formik.touched.name && !!formik.errors.name}
        />
        {formik.touched.name && formik.errors.name && (
          <ErrorMsg>{formik.errors.name}</ErrorMsg>
        )}
      </div>

      {/* Email */}
      <div>
        <Input
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          placeholder="البريد الالكتروني"
          hasError={formik.touched.email && !!formik.errors.email}
        />
        {formik.touched.email && formik.errors.email && (
          <ErrorMsg>{formik.errors.email}</ErrorMsg>
        )}
      </div>

      {/* Phone */}
      <div>
        <Input
          name="phone_number"
          value={formik.values.phone_number}
          onChange={formik.handleChange}
          placeholder="رقم الهاتف"
          hasError={formik.touched.phone_number && !!formik.errors.phone_number}
        />
        {formik.touched.phone_number && formik.errors.phone_number && (
          <ErrorMsg>{formik.errors.phone_number}</ErrorMsg>
        )}
      </div>

      {/* Password */}
      <div style={{ position: "relative" }}>
        <Input
          name="password"
          type={showPassword ? "text" : "password"}
          value={formik.values.password}
          onChange={formik.handleChange}
          placeholder="كلمة المرور"
          hasError={formik.touched.password && !!formik.errors.password}
        />
        <span
          onClick={() => setShowPassword((prev) => !prev)}
          style={{
            position: "absolute",
            top: "50%",
            right: "10px",
            transform: "translateY(-50%)",
            cursor: "pointer",
          }}
        >
          {showPassword ? <FiEyeOff /> : <FiEye />}
        </span>
        {formik.touched.password && formik.errors.password && (
          <ErrorMsg>{formik.errors.password}</ErrorMsg>
        )}
      </div>

      {/* Confirm Password */}
      <div style={{ position: "relative" }}>
        <Input
          name="confirm_password"
          type={showConfirmPassword ? "text" : "password"}
          value={formik.values.confirm_password}
          onChange={formik.handleChange}
          placeholder="تأكيد كلمة المرور"
          hasError={
            formik.touched.confirm_password &&
            !!formik.errors.confirm_password
          }
        />
        <span
          onClick={() => setShowConfirmPassword((prev) => !prev)}
          style={{
            position: "absolute",
            top: "50%",
            right: "10px",
            transform: "translateY(-50%)",
            cursor: "pointer",
          }}
        >
          {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
        </span>
        {formik.touched.confirm_password &&
          formik.errors.confirm_password && (
            <ErrorMsg>{formik.errors.confirm_password}</ErrorMsg>
          )}
      </div>

      {/* Profile Image Upload */}
      <AddPhoto>
        <HiddenFileInput
          type="file"
          id="profile_img"
          name="profile_img"
          accept="image/*"
          onChange={(e) =>
            formik.setFieldValue("profile_img", e.currentTarget.files[0])
          }
        />
        <PhotoLabel htmlFor="profile_img">📷</PhotoLabel>
        <p>
          أضف <br /> صورة شخصية
        </p>
      </AddPhoto>
      {formik.touched.profile_img && formik.errors.profile_img && (
        <ErrorMsg>{formik.errors.profile_img}</ErrorMsg>
      )}

      <PopupButton type="submit">إضافة حساب جديد</PopupButton>
    </AccountPopup>
  );
};

export default SignupForm;
